import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, Type, Image, Link as LinkIcon, Save, RefreshCw, Eye, EyeOff, X
} from "lucide-react";



const SERVER_URL = "https://allinone-clone-7d5d795e98ce.herokuapp.com";

const CTASectionAdmin = () => {
  const [data, setData] = useState({
    title: "",
    description: "",
    button: { text: "", link: "" },
    images: {
      background: "",
      main: "",
      secondary: "",
      shape1: "",
      shape2: ""
    },
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/cta-section`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        showMessage("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Handle Simple Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  // Handle Nested Object Change (Button, Images)
  const handleNestedChange = (category, field, value) => {
    setData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`${SERVER_URL}/api/cta-section`, data);
      showMessage("Section updated successfully!", "success");
    } catch (err) {
      showMessage("Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to default settings?")) return;
    try {
      const res = await axios.post(`${SERVER_URL}/api/cta-section/reset`);
      if (res.data.success) {
        setData(res.data.data);
        showMessage("Reset successful", "success");
      }
    } catch (err) {
      showMessage("Failed to reset", "error");
    }
  };

  const toggleStatus = async () => {
    try {
      await axios.patch(`${SERVER_URL}/api/cta-section/toggle`);
      const newStatus = !data.isActive;
      setData({ ...data, isActive: newStatus });
      showMessage(`Section ${newStatus ? "activated" : "deactivated"}`, "success");
    } catch (err) {
      showMessage("Failed to toggle status", "error");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Admin Panel...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">CTA Section Management</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${data.isActive ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={toggleStatus}
            >
                {data.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                {data.isActive ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset} disabled={saving}>
            <RefreshCw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : message.type} mb-4`}>
          {message.text}
          <button type="button" className="btn-close float-end" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* Content Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Type size={20} /> Content
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Title</label>
                        <input
                          type="text"
                          name="title"
                          className="form-control"
                          value={data.title}
                          onChange={handleChange}
                        />
                    </div>
                    <div className="mb-0">
                        <label className="form-label small fw-bold">Description</label>
                        <textarea
                          name="description"
                          className="form-control"
                          rows="3"
                          value={data.description}
                          onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
          </div>

          {/* Button Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <LinkIcon size={20} /> Call to Action Button
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3 mb-md-0">
                            <label className="form-label small fw-bold">Button Text</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.button?.text || ""}
                              onChange={(e) => handleNestedChange('button', 'text', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label small fw-bold">Button Link</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.button?.link || ""}
                              onChange={(e) => handleNestedChange('button', 'link', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Images Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Image size={20} /> Images & Shapes
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Main Image</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.images?.main || ""}
                              onChange={(e) => handleNestedChange('images', 'main', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Secondary Image</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.images?.secondary || ""}
                              onChange={(e) => handleNestedChange('images', 'secondary', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default CTASectionAdmin;